import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import {
  faEllipsisH,
  faSearch,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { EngagementOption } from "backend/model/engagementOption";
import { EngagementPurposeType } from "backend/model/engagementPurposeType";
import { EngagementRole } from "backend/model/engagementRole";
import { EngagementState } from "backend/model/engagementState";
import {
  EngagementTitle,
  _EngagementTitleFields as EngagementTitleFields,
} from "backend/model/engagementTitle";
import { TeamMemberStatus } from "backend/model/teamMemberStatus";
import { TreeNode } from "primeng/api";
import { DynamicDialogRef } from "primeng/dynamicdialog";
import { IS_CLASSIC_ENGAGEMENT_KEY } from "src/app/classic/classic-interceptor";
import { injectAppQueryTableService } from "src/app/query/app-query-table.service";
import { IChangeState } from "src/app/services/edit-watcher.service";
import { EngagementService } from "src/app/services/engagement.service";
import { FavoriteEngagementWatcherService } from "src/app/workspace/workspace-list/favorite-engagement-watcher.service";
import {
  ICopyCloneAndRollforwardModalProps,
  WorkspaceListTreeComponent,
} from "src/app/workspace/workspace-list/workspace-list-tree/workspace-list-tree.component";
import { WorkspaceListService } from "src/app/services/workspace-list.service";
import { WorkspacePageContentComponent } from "src/app/workspace/workspace-page-content/workspace-page-content.component";
import { FORMAT_EMPTY, MAX_SAFE_INTEGER, defaultRowsPerPage } from "src/config";
import { AnnotateActiveCodePipe } from "src/standard/pipes/annotate-active-code.pipe";
import { NoopSaveContext } from "src/standard/services/NoopSaveContext";
import { addQueryParams } from "src/standard/services/addQueryParams";
import { FocusService } from "src/standard/services/focus.service";
import { getQueryParams } from "src/standard/services/getQueryParams";
import { injectModalPage } from "src/standard/services/modal-page.service";
import { IHaveChildDialogRefs } from "src/standard/services/pending-changes.service";
import { removeQueryParams } from "src/standard/services/removeQueryParams";
import { StandardButtonDropdownActionsComponent } from "src/standard/standard-button-dropdown/standard-button-dropdown-actions/standard-button-dropdown-actions.component";
import {
  IStandardButtonDropdownItem,
  StandardButtonDropdownComponent,
} from "src/standard/standard-button-dropdown/standard-button-dropdown.component";
import { StandardButtonComponent } from "src/standard/standard-button/standard-button.component";
import { StandardFieldWrapperComponent } from "src/standard/standard-field-wrapper/standard-field-wrapper.component";
import { WindowDialogService } from "src/standard/standard-modal-button/showWindowDialog";
import { StandardQueryErrorComponent } from "src/standard/standard-query-error/standard-query-error.component";
import { StandardTextInputComponent } from "src/standard/standard-text-input/standard-text-input.component";
import { StandardTooltip } from "src/standard/standard-tooltip/standard-tooltip.component";
import { OrderByString } from "src/standard/ts/apiUtils";
import { injectForm } from "src/standard/ts/formUtils";
import { FILTER_MODE_ENUM } from "src/standard/ts/types";
import { IEngagementTitle } from "backend/model/iEngagementTitle";
import {
  WorkspaceSearchData,
  WorkspaceSearchResultsContext,
  WorkspaceSearchResultsResponse,
} from "src/app/workspace/workspace-search-results/workspace-search-results.component";
import {
  APTContext,
  SUPPORT_SELECTED_MEMBER_FIRM_ID,
} from "src/app/services/aptContext/apt-context";
import {
  createObservableBroadcastChannel,
  ObservableBroadcastChannel,
} from "src/app/services/aptContext/broadcast-channels";
import { Subject } from "rxjs";
import { SignalRService } from "src/app/services/signalr/signalr.service";
import {
  SIGNAL_R_CONSTANTS_CHANGE_ENGAGEMENT_COMMAND,
  SIGNAL_R_CONSTANTS_LOCK_ENGAGEMENT_COMMAND,
  SIGNAL_R_CONSTANTS_RESTORE_ENGAGEMENT_FROM_REMOTE_STORAGE_COMMAND,
} from "src/app/services/signalr/signalr-constants";
import { WorkspaceWindowTitleComponent } from "src/app/workspace/workspace-window-title/workspace-window-title.component";
import { precacheComponent } from "src/standard/ts/prerun/precacheComponent";
import { SnapshotManagementService } from "src/app/services/snapshot-management.service";
import { StandardDropdownSingleComponent } from "src/standard/standard-dropdown-single/standard-dropdown-single.component";
import { Router } from "@angular/router";
import { RemoteStorageStates } from "src/app/services/RemoteStorageStates";

const FILTERS_KEY = "WorkspaceListFilters";
const DEFAULT_PAGE_SORT: OrderByString<IWorkspaceRowData> = "clientName asc";

const EntityFields = EngagementTitleFields;

export const WORKSPACE_LIST_DEFAULT_SORT = "clientName asc, endDate desc";

const SortOptions = {
  startDate: "startDate desc, clientName asc",
  endDate: "endDate desc, clientName asc",
  clientName: WORKSPACE_LIST_DEFAULT_SORT,
  archivedDate: "archivedDate desc, clientName asc",
  engagementName: "engagementName asc, endDate desc",
} satisfies Record<SortCode, string>;

export const FavoritesLabel = $localize`Favourites`;

export const APTVersion = {
  NextGen: "nextGen" as const satisfies APTVersionType,
  Classic: "classic" as const satisfies APTVersionType,
};
type MemberFirmOption = {
  label: string;
  value: string;
  name: string;
};
type SortCode =
  | "startDate"
  | "endDate"
  | "clientName"
  | "archivedDate"
  | "engagementName";

export type IWorkspaceRowData = EngagementTitle & IChangeState;

export type APTVersionType = "nextGen" | "classic";

export type WorkspaceListGroupCode = "None" | "Client";

export type WorkspaceFilterCode =
  | "All"
  | EngagementState
  | EngagementPurposeType
  | "Removed"
  | "RecentlyOpened"
  | "Unopened"
  | "EngagementSnapshot";

export type WorkspaceTreeNodeData = {
  clientId: string | undefined;
  engagementId: string | undefined;
  subLabel?: string;
  archiveDeadline?: string;
  purpose?: EngagementPurposeType;
  state?: EngagementState;
  status?: TeamMemberStatus;
  role?: EngagementRole;
  unopened?: boolean;
  endDate?: string;
  memberFirmId?: number;
  libraryVersion?: number;
  lockedTooltip?: string;
  isCrossBorderBlockListEngagement?: boolean;
  copiedAt?: string;
  offlineText?: string;
};

export type WorkspaceTreeNode = Omit<
  TreeNode<WorkspaceTreeNodeData>,
  "children" | "icon" | "styleClass" | "data"
> & {
  children?: WorkspaceTreeNode[];
  data: WorkspaceTreeNodeData;
};

const engagementStates: EngagementState[] = [
  "None",
  "Active",
  "Locked",
  "EngagementSignedOff",
  "Archived",
  "PlanningSignedOff",
  "Deleted",
  "Archiving",
  "MarkedForDeletion",
  "RemoteStorage",
  "InTransit",
  "InTransitFailed",
  "Restoring",
  "RestoreFailed",
  "DeletionPending",
  "EngagementDeletionFailed",
];

interface SearchFormData {
  selectedMemberFirmId: string;
  clientId: string;
  clientName: string;
  entityName: string;
  workspaceDisplayName: string;
  engagementId: string;
}

function collapseNodesRecursive(
  node: WorkspaceTreeNode,
  listItemStates: { [key: string]: boolean },
) {
  node.expanded = false;

  if (node.children) {
    if (node.label) {
      listItemStates[node.label] = node.expanded;
    }

    for (const child of node.children) {
      collapseNodesRecursive(child, listItemStates);
    }
  }
}

function expandNodesRecursive(
  node: WorkspaceTreeNode,
  listItemStates: { [key: string]: boolean },
) {
  node.expanded = true;
  if (node.children) {
    if (node.label) {
      listItemStates[node.label] = node.expanded;
    }

    for (const child of node.children) {
      expandNodesRecursive(child, listItemStates);
    }
  }
}

export function sortEngagements(
  engagements: EngagementTitle[] | IEngagementTitle[],
  sortCode: SortCode,
) {
  const criteria = SortOptions[sortCode].split(", ").map((criterion) => {
    const [property, order] = criterion.split(" ");
    return { property, order };
  });

  engagements.sort((a, b) => {
    for (const { property, order } of criteria) {
      let propA = a[property as keyof (EngagementTitle | IEngagementTitle)];
      let propB = b[property as keyof (EngagementTitle | IEngagementTitle)];

      if (property === "clientName") {
        propA = propA ? propA.toString().toUpperCase() : undefined;
        propB = propB ? propB.toString().toUpperCase() : undefined;
      }

      if (property === "archivedDate") {
        propA = propA ? propA : "1800-01-01T00:00:00Z";
        propB = propB ? propB : "1800-01-01T00:00:00Z";
      }

      const defaultVal = order === "asc" ? Infinity : -Infinity;

      const valueA = propA !== undefined ? propA : defaultVal;
      const valueB = propB !== undefined ? propB : defaultVal;

      if (valueA < valueB) {
        return order === "asc" ? -1 : 1;
      }
      if (valueA > valueB) {
        return order === "asc" ? 1 : -1;
      }
    }
    return 0;
  });
}

function filterEngagementsAll(
  engagements: EngagementTitle[],
  isSupportUser: boolean,
): EngagementTitle[] {
  const statuses: string[] = [
    TeamMemberStatus.Active,
    TeamMemberStatus.Invited,
  ];

  if (isSupportUser) {
    statuses.push(TeamMemberStatus.None);
  }

  return engagements.filter((engagement) => {
    return statuses.includes(engagement.status);
  });
}

function filterEngagementsRemoved(
  engagements: EngagementTitle[],
  isSupportUser: boolean,
): EngagementTitle[] {
  const statuses: string[] = [
    TeamMemberStatus.InvitedInactive,
    TeamMemberStatus.Inactive,
  ];

  if (isSupportUser) {
    statuses.push(TeamMemberStatus.None);
  }

  return engagements.filter((engagement) => {
    return (
      statuses.includes(engagement.status) &&
      !RemoteStorageStates.includes(engagement.state)
    );
  });
}

function filterEngagementsRecentlyOpened(
  engagements: EngagementTitle[],
): EngagementTitle[] {
  engagements = engagements
    .filter((engagement) => engagement.status === EngagementState.Active)
    .sort(
      (a, b) =>
        new Date(b.lastOpened ?? 0).getTime() -
        new Date(a.lastOpened ?? 0).getTime(),
    );

  if (engagements.length > 5) {
    engagements = engagements.slice(0, 5);
  }

  return engagements;
}

function filterEngagementsUnopened(
  engagements: EngagementTitle[],
): EngagementTitle[] {
  return engagements.filter((engagement) => {
    return engagement.status === TeamMemberStatus.Invited;
  });
}

function filterEngagementsTrainingPreview(
  engagements: EngagementTitle[],
  engagementPurpose: string,
  isSupportUser: boolean,
): EngagementTitle[] {
  const statuses: string[] = [
    TeamMemberStatus.Active,
    TeamMemberStatus.Invited,
  ];

  if (isSupportUser) {
    statuses.push(TeamMemberStatus.None);
  }

  return engagements.filter((engagement) => {
    return (
      statuses.includes(engagement.status) &&
      engagement.engagementPurpose === engagementPurpose
    );
  });
}

function filterEngagementsActive(
  engagements: EngagementTitle[],
  isSupportUser: boolean,
): EngagementTitle[] {
  const statuses: string[] = [
    TeamMemberStatus.Active,
    TeamMemberStatus.Invited,
  ];
  const archivedStates = new Set<EngagementState>([
    EngagementState.Archived,
    EngagementState.Archiving,
  ]);

  if (isSupportUser) {
    statuses.push(TeamMemberStatus.None);
  }

  return engagements.filter((engagement) => {
    return (
      statuses.includes(engagement.status) &&
      !archivedStates.has(engagement.state) &&
      !RemoteStorageStates.includes(engagement.state)
    );
  });
}

function filterEngagementsWithSnapshots(
  engagements: EngagementTitle[],
): EngagementTitle[] {
  return engagements.filter((engagement) => {
    return engagement.hasEngagementSnapshot;
  });
}

function filterEngagementsRemoteStorage(
  engagements: EngagementTitle[],
): EngagementTitle[] {
  return engagements.filter((engagement) => {
    return RemoteStorageStates.includes(engagement.state);
  });
}

function filterEngagementsArchived(
  engagements: EngagementTitle[],
  isSupportUser: boolean,
): EngagementTitle[] {
  const states = new Set<EngagementState>([
    EngagementState.Archived,
    EngagementState.Archiving,
  ]);
  const statuses: string[] = [
    TeamMemberStatus.Active,
    TeamMemberStatus.Invited,
  ];

  if (isSupportUser) {
    statuses.push(...[TeamMemberStatus.None]);
  }

  return engagements.filter((engagement) => {
    return statuses.includes(engagement.status) && states.has(engagement.state);
  });
}

function filterEngagementsLocked(
  engagements: EngagementTitle[],
  isSupportUser: boolean,
): EngagementTitle[] {
  const statuses: string[] = [
    TeamMemberStatus.Active,
    TeamMemberStatus.Invited,
  ];

  if (isSupportUser) {
    statuses.push(...[TeamMemberStatus.None]);
  }

  return engagements.filter((engagement) => {
    return (
      statuses.includes(engagement.status) &&
      engagement.state === EngagementState.Locked
    );
  });
}

@Component({
  standalone: true,
  selector: "app-workspace-list",
  templateUrl: "./workspace-list.component.html",
  styles: `
    .favorites-tree {
      background-color: var(--bs-panel-subheader-bg);
    }

    ::ng-deep .favorites-tree .cdk-virtual-scrollable {
      // idk
      contain: none;
    }
  `,
  host: {
    "[attr.inert]":
      "workspaceListService.isViewingCloneCopyRollforward() ? '' : undefined",
  },
  imports: [
    FormsModule,
    ReactiveFormsModule,
    StandardTooltip,
    StandardButtonComponent,
    StandardButtonDropdownComponent,
    StandardButtonDropdownActionsComponent,
    StandardFieldWrapperComponent,
    StandardTextInputComponent,
    StandardDropdownSingleComponent,
    StandardQueryErrorComponent,
    WorkspacePageContentComponent,
    WorkspaceListTreeComponent,
    AnnotateActiveCodePipe,
    WorkspaceWindowTitleComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkspaceListComponent
  implements OnInit, OnDestroy, IHaveChildDialogRefs
{
  protected host = inject<ElementRef<HTMLElement>>(ElementRef);
  private router = inject(Router);

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  __settings = globalThis.__settings!;

  MAX_SAFE_INTEGER = MAX_SAFE_INTEGER;

  faXmark = faXmark;
  faSearch = faSearch;
  faEllipsisH = faEllipsisH;

  protected readonly hideSearchText = $localize`Hide search`;
  protected readonly showSearchText = $localize`Show search`;

  private allEngagementsBC: ObservableBroadcastChannel<string>;
  private recentlyOpenedEngagementsBC: ObservableBroadcastChannel;

  private initialLoad = true;
  private canBeFinishedLoading = false;
  private getFilteredItemsHasRan = false;
  private userContextHasBeenRefreshed = false;
  private workspaceSearchRef: DynamicDialogRef | undefined;
  private searchDialogOpening = false;
  private reSearch = new Subject<WorkspaceSearchData>();

  private readonly needsRefresh = signal<boolean>(false);
  protected readonly searchIconHovered = signal<boolean>(false);
  readonly loading = signal<boolean>(true);
  protected readonly aptVersion = signal<APTVersionType>(APTVersion.NextGen);
  private readonly isClassicEngagementSupported = computed(() => {
    return this.aptContext.isClassicEngagementSupported();
  });
  protected readonly isSupportUser = computed(() => {
    return this.aptContext.isSupportUser() ?? false;
  });
  onboardedFirms: MemberFirmOption[] = [];

  readonly activeFilterData = signal<
    | {
        filterCode: WorkspaceFilterCode;
        sortCode: SortCode;
        groupCode: WorkspaceListGroupCode;
      }
    | undefined
  >(undefined);
  // Track the tree separately so we can expand/collapse easily
  readonly isSearchVisible = signal<boolean>(false);
  protected readonly selectedEngagements = signal<WorkspaceTreeNode[]>([]);
  public readonly childDialogRefs = signal<DynamicDialogRef<unknown>[]>([]);

  private readonly snapshotManagementService = inject(
    SnapshotManagementService,
  );
  private readonly focusService = inject(FocusService);
  private readonly engagementService = inject(EngagementService);
  private readonly windowDialogService = inject(WindowDialogService);
  protected readonly workspaceListService = inject(WorkspaceListService);
  private readonly favoriteEngagementWatcher = inject(
    FavoriteEngagementWatcherService,
  );
  protected readonly signalRService = inject(SignalRService);

  private readonly destroyRef = inject(DestroyRef);

  title = computed(() => {
    const isSearchVisible = this.isSearchVisible();
    if (isSearchVisible) {
      return $localize`Search`;
    }
    return $localize`Workspaces`;
  });

  private aptContext = inject(APTContext);
  activeCodes = computed(() => {
    return {
      filterCode: this.activeFilterCode(),
      sortCode: this.activeSortCode(),
      groupCode: this.activeGroupCode(),
    };
  });

  activeFilterCode = computed(() => {
    const data = this.activeFilterData();
    return data?.filterCode ?? "Active";
  });

  activeFilterLabel = computed(() => {
    const code = this.activeFilterCode();
    const item = this.filterMenuItems().find((x) => x.code === code);
    return (item as { label: string } | undefined)?.label ?? "";
  });

  activeSortCode = computed(() => {
    const data = this.activeFilterData();
    return data?.sortCode ?? "clientName";
  });

  activeSortLabel = computed(() => {
    const code = this.activeSortCode();
    const item = this.sortMenuItems.find((x) => x.code === code);
    return item?.label ?? "";
  });

  activeGroupCode = computed(() => {
    const data = this.activeFilterData();
    return data?.groupCode ?? "Client";
  });

  activeGroupLabel = computed(() => {
    const code = this.activeGroupCode();
    const item = this.groupMenuItems.find((x) => x.code === code);
    return item?.label ?? "";
  });

  activeFilterTransitionState = computed(() => {
    return this.engagementsTableService.listFormGroup.transitionState();
  });

  activeFilterCount = computed(() => {
    const data = this.engagementsTableService.actionData();
    return data?.totalRows;
  });
  canViewEngagementSnapshots = computed(() =>
    this.snapshotManagementService.canViewEngagementSnapshots(),
  );

  initIsClassicEngagement: string | null = null;

  constructor() {
    // If the user is viewing an engagement, save whether the session storage currently
    // reflects if the user is in a classic engagement or not
    const currentUrl = this.router.url;
    const lastOfRoute = currentUrl.split("/").at(-1);
    if (lastOfRoute !== "ws") {
      this.initIsClassicEngagement = sessionStorage.getItem(
        IS_CLASSIC_ENGAGEMENT_KEY,
      );
    }

    sessionStorage.setItem(IS_CLASSIC_ENGAGEMENT_KEY, "false");

    const filterDataString = localStorage.getItem(FILTERS_KEY);
    if (filterDataString) {
      this.activeFilterData.set(
        JSON.parse(filterDataString) as {
          filterCode: WorkspaceFilterCode;
          sortCode: SortCode;
          groupCode: WorkspaceListGroupCode;
        },
      );
    }

    this.workspaceListService.modalPage = this.modalPage;
    // Subscribe to table submission updates
    this.engagementsTableService.trySubmit
      .pipe(takeUntilDestroyed())
      .subscribe((isNew) => {
        void this.onSubmit(isNew);
      });

    this.allEngagementsBC = createObservableBroadcastChannel("allEngagements");
    this.allEngagementsBC.obs$
      .pipe(takeUntilDestroyed())
      .subscribe(async () => {
        await this.onSubmit(true);
      });

    this.recentlyOpenedEngagementsBC =
      createObservableBroadcastChannel("engagement-watcher");
    this.recentlyOpenedEngagementsBC.obs$
      .pipe(takeUntilDestroyed())
      .subscribe(async () => {
        await this.onSubmit(true);
      });
  }

  protected modalPage = injectModalPage(new NoopSaveContext(), () => ``, {});

  searchForm = injectForm<SearchFormData>({
    selectedMemberFirmId: "",
    clientId: "",
    clientName: "",
    entityName: "",
    workspaceDisplayName: "",
    engagementId: "",
  });

  protected engagementsTableService =
    injectAppQueryTableService<IWorkspaceRowData>({
      orderBy: DEFAULT_PAGE_SORT,
      pageSize: defaultRowsPerPage,
      queryEntityFields: EntityFields,
      entityIdFieldName: "engagementId",
      filterMode: FILTER_MODE_ENUM.Server,
      getFilteredItems: async () => {
        this.loading.set(true);
        let engagements = this.engagementService.allEngagements();

        if (!engagements) {
          return {
            items: [],
            pageNumber: 1,
            pageSize: 0,
            totalRows: 0,
          };
        }

        if (engagements.length === 0) {
          this.getFilteredItemsHasRan = true;
          await this.aptContext.refetchUserContext();
          this.userContextHasBeenRefreshed = true;

          if (
            this.aptVersion() === APTVersion.NextGen &&
            !this.isSupportUser() &&
            !this.aptContext.isOfflineWebMode
          ) {
            void this.showAddWorkspace(EngagementOption.Create);
          }

          return {
            items: [],
            pageNumber: 1,
            pageSize: 0,
            totalRows: 0,
          };
        }

        engagements = this.filterEngagements(engagements);

        sortEngagements(engagements, this.activeSortCode());
        this.getFilteredItemsHasRan = true;
        return {
          items: engagements as IWorkspaceRowData[],
          pageNumber: 1,
          pageSize: engagements.length,
          totalRows: engagements.length,
        };
      },
      postGetFilteredItems: async () => {
        if (!this.userContextHasBeenRefreshed) {
          await this.aptContext.refetchUserContext();
          this.userContextHasBeenRefreshed = true;
        }
      },
    });

  readonly favoriteWorkspaces = computed<WorkspaceTreeNode[]>(() => {
    // favorites are disabled in classic APT
    if (this.aptVersion() === APTVersion.Classic) {
      return [];
    }

    const items = this.engagementsTableService.actionData()?.items;
    const allEngagements = this.engagementService.allEngagements();
    if (!items || !allEngagements) {
      return [];
    }

    const favoriteEngagementIds =
      this.favoriteEngagementWatcher.favoriteEngagementIds();
    let favoriteEngagements: EngagementTitle[] = [];

    if (favoriteEngagementIds.length > 0) {
      favoriteEngagements = allEngagements.filter((engagement) => {
        return (
          engagement.engagementId &&
          favoriteEngagementIds.includes(engagement.engagementId)
        );
      });

      sortEngagements(favoriteEngagements, this.activeSortCode());
    }

    const favoriteWorkspaces: WorkspaceTreeNode[] = [];
    for (const favoriteEngagement of favoriteEngagements) {
      const workspaceTreeNode =
        this.workspaceListService.createWorkspaceTreeNode(favoriteEngagement);
      favoriteWorkspaces.push(workspaceTreeNode);
    }

    const favoritesNode = {
      label: FavoritesLabel,
      children: favoriteWorkspaces,
      expanded: this.workspaceListService.getListItemState(FavoritesLabel),
      selectable: false,
      data: {
        clientId: undefined,
        engagementId: undefined,
      },
    };

    return [favoritesNode];
  });

  readonly groupedWorkspaces = computed<WorkspaceTreeNode[]>(() => {
    const items = this.engagementsTableService.actionData()?.items;
    const allEngagements = this.engagementService.allEngagements();
    if (!items || !allEngagements) {
      return [];
    }

    const activeGroupCode = this.activeGroupCode();
    switch (activeGroupCode) {
      case "Client": {
        // Group items by clientId/clientName
        const clientIdLookup: Record<string, string> = {};
        const groupedItems = new Map<string, typeof items>();
        for (const item of items) {
          const clientId = item.clientId ?? FORMAT_EMPTY;
          const clientName = (item.clientName ?? FORMAT_EMPTY).toUpperCase();
          const key = clientName;
          const existingItems = groupedItems.get(key) ?? [];
          existingItems.push(item);
          groupedItems.set(key, existingItems);

          clientIdLookup[clientName] = clientId;
        }

        // Transform items into tree nodes
        const workspaceTreeData: WorkspaceTreeNode[] = [];
        for (const [clientName, clientItems] of groupedItems) {
          const workspaceNodes: WorkspaceTreeNode[] = [];
          for (const item of clientItems) {
            const workspaceNode =
              this.workspaceListService.createWorkspaceTreeNode(item);
            workspaceNodes.push(workspaceNode);
          }

          const clientId = clientIdLookup[clientName];
          const clientNode: WorkspaceTreeNode = {
            label: clientName,
            children: workspaceNodes,
            expanded: this.workspaceListService.getListItemState(clientName),
            selectable: false,
            data: {
              clientId,
              engagementId: undefined,
            },
          };
          workspaceTreeData.push(clientNode);
        }

        this.canBeFinishedLoading = true;

        return [...workspaceTreeData];
      }

      case "None": {
        // Transform items into tree nodes
        const workspaceTreeData: WorkspaceTreeNode[] = [];
        for (const item of items) {
          const workspaceNode =
            this.workspaceListService.createWorkspaceTreeNode(item);
          workspaceTreeData.push(workspaceNode);
        }

        this.canBeFinishedLoading = true;

        return [...workspaceTreeData];
      }

      default:
        // This switch should be exhaustive.
        // If you forgot one above, the below will not compile.
        return activeGroupCode satisfies never;
    }
  });

  private syncWorkspaceTreeData = effect(() => {
    // trigger when changed
    this.favoriteWorkspaces();
    this.groupedWorkspaces();

    if (
      this.engagementService.allEngagements() &&
      this.getFilteredItemsHasRan &&
      this.canBeFinishedLoading
    ) {
      this.loading.set(false);
    }
  });

  // Re-trigger search when filter data changes
  private refreshWhenFiltersChange = effect(() => {
    this.activeFilterCode();
    this.activeSortCode();
    this.activeGroupCode();
    if (!this.initialLoad) {
      this.loading.set(true);
      void this.onSubmit(true);
      const filterData = this.activeFilterData();
      if (filterData) {
        localStorage.setItem(FILTERS_KEY, JSON.stringify(filterData));
      }
    } else {
      this.initialLoad = false;
    }
  });

  readonly filterMenuItems = computed(() => {
    const allEngagements = this.engagementService.allEngagements() ?? [];
    const defaultCount = this.engagementService.allEngagements()
      ? undefined
      : "--";

    const allCount = filterEngagementsAll(
      allEngagements,
      this.isSupportUser(),
    ).length;
    const activeCount = filterEngagementsActive(
      allEngagements,
      this.isSupportUser(),
    ).length;
    const archivedCount = filterEngagementsArchived(
      allEngagements,
      this.isSupportUser(),
    ).length;
    const removedCount = filterEngagementsRemoved(
      allEngagements,
      this.isSupportUser(),
    ).length;
    const recentlyOpenedCount =
      filterEngagementsRecentlyOpened(allEngagements).length;
    const unopenedCount = filterEngagementsUnopened(allEngagements).length;
    const trainingCount = filterEngagementsTrainingPreview(
      allEngagements,
      EngagementPurposeType.Training,
      this.isSupportUser(),
    ).length;
    const previewCount = filterEngagementsTrainingPreview(
      allEngagements,
      EngagementPurposeType.Preview,
      this.isSupportUser(),
    ).length;
    const lockedCount = filterEngagementsLocked(
      allEngagements,
      this.isSupportUser(),
    ).length;
    const remoteStorageCount =
      filterEngagementsRemoteStorage(allEngagements).length;
    const snapshotCount = filterEngagementsWithSnapshots(allEngagements).length;

    const filterMenuItems = [
      {
        code: "All",
        label: $localize`All (${defaultCount ?? allCount})`,
        keyboardShortcut: true,
        command: () => {
          this.activeFilterData.set({
            ...this.activeCodes(),
            filterCode: "All",
          });
        },
      },
      {
        code: "Active",
        label: $localize`Active (${defaultCount ?? activeCount})`,
        keyboardShortcut: true,
        command: () => {
          this.activeFilterData.set({
            ...this.activeCodes(),
            filterCode: EngagementState.Active,
          });
        },
      },
      {
        code: "Archived",
        label: $localize`Archived (${defaultCount ?? archivedCount})`,
        keyboardShortcut: true,
        command: () => {
          this.activeFilterData.set({
            ...this.activeCodes(),
            filterCode: EngagementState.Archived,
          });
        },
      },
      {
        code: "Removed",
        label: $localize`Removed (${defaultCount ?? removedCount})`,
        keyboardShortcut: true,
        command: () => {
          this.activeFilterData.set({
            ...this.activeCodes(),
            filterCode: "Removed",
          });
        },
      },
      {
        type: "divider",
      },
      {
        code: "RecentlyOpened",
        label: $localize`Recently opened (${defaultCount ?? recentlyOpenedCount})`,
        keyboardShortcut: true,
        command: () => {
          this.activeFilterData.set({
            ...this.activeCodes(),
            filterCode: "RecentlyOpened",
          });
        },
      },
      {
        code: "Unopened",
        label: $localize`Unopened (${defaultCount ?? unopenedCount})`,
        keyboardShortcut: true,
        command: () => {
          this.activeFilterData.set({
            ...this.activeCodes(),
            filterCode: "Unopened",
          });
        },
      },
      {
        code: "Training",
        label: $localize`Training (${defaultCount ?? trainingCount})`,
        keyboardShortcut: true,
        command: () => {
          this.activeFilterData.set({
            ...this.activeCodes(),
            filterCode: EngagementPurposeType.Training,
          });
        },
      },
      {
        code: "Preview",
        label: $localize`Preview (${defaultCount ?? previewCount})`,
        keyboardShortcut: true,
        command: () => {
          this.activeFilterData.set({
            ...this.activeCodes(),
            filterCode: EngagementPurposeType.Preview,
          });
        },
      },
      {
        type: "divider",
      },
      {
        code: "Locked",
        label: $localize`Locked (${defaultCount ?? lockedCount})`,
        keyboardShortcut: true,
        command: () => {
          this.activeFilterData.set({
            ...this.activeCodes(),
            filterCode: EngagementState.Locked,
          });
        },
      },
      {
        code: "RemoteStorage",
        label: $localize`Remote storage (${defaultCount ?? remoteStorageCount})`,
        keyboardShortcut: true,
        command: () => {
          this.activeFilterData.set({
            ...this.activeCodes(),
            filterCode: EngagementState.RemoteStorage,
          });
        },
      },
    ] satisfies (IStandardButtonDropdownItem & { code?: string })[];

    if (this.snapshotManagementService.canViewEngagementSnapshots()) {
      filterMenuItems.push({
        code: "EngagementSnapshot",
        label: $localize`Engagement snapshot (${defaultCount ?? snapshotCount})`,
        keyboardShortcut: true,
        command: () => {
          this.activeFilterData.set({
            ...this.activeCodes(),
            filterCode: "EngagementSnapshot",
          });
        },
      });
    }

    return filterMenuItems;
  });

  sortMenuItems = [
    {
      code: "startDate",
      label: $localize`Start date`,
      keyboardShortcut: true,
      command: () => {
        this.activeFilterData.set({
          ...this.activeCodes(),
          sortCode: "startDate",
        });
      },
    },
    {
      code: "endDate",
      label: $localize`End date`,
      keyboardShortcut: true,
      command: () => {
        this.activeFilterData.set({
          ...this.activeCodes(),
          sortCode: "endDate",
        });
      },
    },
    {
      code: "clientName",
      label: $localize`Client`,
      keyboardShortcut: true,
      command: () => {
        this.activeFilterData.set({
          ...this.activeCodes(),
          sortCode: "clientName",
        });
      },
    },
    {
      code: "archivedDate",
      label: $localize`Archive date`,
      keyboardShortcut: true,
      command: () => {
        this.activeFilterData.set({
          ...this.activeCodes(),
          sortCode: "archivedDate",
        });
      },
    },
  ] satisfies (IStandardButtonDropdownItem & { code: SortCode })[];

  groupMenuItems = [
    {
      code: "None",
      label: $localize`None`,
      keyboardShortcut: true,
      command: () => {
        this.activeFilterData.set({
          ...this.activeCodes(),
          groupCode: "None",
        });
      },
    },
    {
      code: "Client",
      label: $localize`Client`,
      keyboardShortcut: true,
      command: () => {
        this.activeFilterData.set({
          ...this.activeCodes(),
          groupCode: "Client",
        });
      },
    },
  ] satisfies (IStandardButtonDropdownItem & { code: string })[];

  addNewWorkspaceMenuItem = computed<IStandardButtonDropdownItem>(() => {
    return {
      label: $localize`Add new workspace`,
      keyboardShortcut: true as const,
      command: () => this.showAddWorkspace(EngagementOption.Create),
      disabled:
        this.isSupportUser() ||
        this.aptVersion() === APTVersion.Classic ||
        this.aptContext.isOfflineWebMode,
    };
  });

  searchActionMenuItems = computed<IStandardButtonDropdownItem[]>(() => {
    return [this.addNewWorkspaceMenuItem()];
  });

  listActionMenuItems = computed<IStandardButtonDropdownItem[]>(() => {
    return [
      this.addNewWorkspaceMenuItem(),
      {
        label: $localize`Restore default view`,
        keyboardShortcut: true,
        command: () => this.resetView(),
      },
      {
        type: "divider",
      },
      {
        label: $localize`Collapse all`,
        keyboardShortcut: true,
        command: () => this.collapseAll(),
      },
      {
        label: $localize`Expand all`,
        keyboardShortcut: true,
        command: () => this.expandAll(),
      },
      {
        type: "divider",
      },
      {
        type: "action",
        // eslint-disable-next-line catalyst/localize-message
        label: `classicOrNextGenChoice`,
        radioGroupOptions: [
          {
            label: $localize`APT Next Gen`,
            value: APTVersion.NextGen,
          },
          {
            label: $localize`APT Classic`,
            value: APTVersion.Classic,
          },
        ],
        onRadioChange: async (newVal) => {
          this.aptVersion.set(newVal as APTVersionType);
          sessionStorage.setItem(
            IS_CLASSIC_ENGAGEMENT_KEY,
            (this.aptVersion() === APTVersion.Classic).toString(),
          );
          await this.handleRefreshWithBCMessage();
          return true;
        },
        radioGroupValue: this.aptVersion(),
        disabled: !this.isClassicEngagementSupported(),
      },
    ];
  });

  readonly favoritesTree =
    viewChild.required<WorkspaceListTreeComponent>("favoritesTree");
  readonly groupsTree =
    viewChild.required<WorkspaceListTreeComponent>("groupsTree");

  async ngOnInit() {
    // Remove the session key for the support user seleted member firm id.
    // This is required any time the workspace list is loaded - Either via
    // going to /ws or by clicking the home button which renders workspace
    // list from within an engagement.  This ensures that the value the
    // support user selected when searching does not impact requests to fetch
    // data if the firm id doesnt match the new requested engagement.
    this.aptContext.removeSupportStorage(SUPPORT_SELECTED_MEMBER_FIRM_ID);

    await this.engagementsTableService.setTransitionStateAsync("pending");

    const queryParams = getQueryParams();
    // If ?search=true in the query params, toggle the view
    const searchQueryParam = queryParams.search?.toLowerCase();
    if (searchQueryParam === "true") {
      this.isSearchVisible.set(true);
    }

    this.signalRService.allMessages$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (message) => {
        if (
          message.$type === SIGNAL_R_CONSTANTS_LOCK_ENGAGEMENT_COMMAND ||
          message.$type === SIGNAL_R_CONSTANTS_CHANGE_ENGAGEMENT_COMMAND
        ) {
          await this.loadMainData();
        } else if (
          message.$type ===
            SIGNAL_R_CONSTANTS_RESTORE_ENGAGEMENT_FROM_REMOTE_STORAGE_COMMAND &&
          message.CurrentState
        ) {
          const currentState = message.CurrentState ?? 0;
          this.handleRestoreEngagement(
            message.EngagementId,
            engagementStates[currentState] ?? EngagementState.None,
          );
        }
      });

    await this.loadMainData();

    if (this.isSupportUser()) {
      await this.loadAllOnboardedMemberFirms();
    }
  }

  ngOnDestroy(): void {
    // If we have it saved that the user is viewing a classic engagement or not,
    // restore the previous value
    if (this.initIsClassicEngagement !== null) {
      sessionStorage.setItem(
        IS_CLASSIC_ENGAGEMENT_KEY,
        this.initIsClassicEngagement,
      );
    }

    this.engagementService.allEngagements.set(undefined);

    this.childDialogRefs().forEach((ref) => {
      ref.close();
    });
  }

  get f() {
    return this.searchForm.controls;
  }

  async loadMainData() {
    this.loading.set(true);
    if (!this.activeFilterData()) {
      this.resetView();
    }
    await this.engagementService.getAllEngagements(
      this.aptVersion() === APTVersion.Classic,
    );
    await this.engagementsTableService.onSubmit(true);
  }

  handleRestoreEngagement(engagementId: string, currentState: EngagementState) {
    const nodeToUpdate = this.groupsTree()
      .treeItems()
      .find((treeItem) => {
        return treeItem.node().data.engagementId === engagementId;
      });
    const engagement = this.engagementsTableService
      .allItems()
      .find((x) => x.engagementId === engagementId);

    // If the engagement is found in the current list, either update its state
    // or remove it if it not longer matches the selected filter
    if (nodeToUpdate && engagement) {
      engagement.state = currentState;
      const matchesFilters = this.filterEngagements([engagement]).length > 0;
      if (matchesFilters) {
        nodeToUpdate.overrideEngagementState.set(currentState);
      } else {
        this.engagementsTableService.removeItem("engagementId", engagement);
      }

      // Update all engagements to trigger recompute of filter counts
      const allEngagements = this.engagementService.allEngagements();
      const changedEngagement = allEngagements?.find(
        (e) => e.engagementId === engagementId,
      );

      if (changedEngagement && allEngagements) {
        changedEngagement.state = currentState;
        this.engagementService.allEngagements.set([...allEngagements]);
      }
    }
  }

  filterEngagements(engagements: EngagementTitle[]): EngagementTitle[] {
    const filterCode = this.activeFilterCode();
    switch (filterCode) {
      case "All":
        return filterEngagementsAll(engagements, this.isSupportUser());

      case "Removed":
        return filterEngagementsRemoved(engagements, this.isSupportUser());

      case "RecentlyOpened":
        return filterEngagementsRecentlyOpened(engagements);

      case "Unopened":
        return filterEngagementsUnopened(engagements);

      case EngagementPurposeType.Training:
      case EngagementPurposeType.Preview:
        return filterEngagementsTrainingPreview(
          engagements,
          filterCode,
          this.isSupportUser(),
        );

      case EngagementState.RemoteStorage:
        return filterEngagementsRemoteStorage(engagements);

      case EngagementState.Active:
        return filterEngagementsActive(engagements, this.isSupportUser());

      case EngagementState.Archived:
        return filterEngagementsArchived(engagements, this.isSupportUser());

      case EngagementState.Locked:
        return filterEngagementsLocked(engagements, this.isSupportUser());

      case "EngagementSnapshot":
        return filterEngagementsWithSnapshots(engagements);

      default:
        return engagements;
    }
  }

  resetView() {
    this.activeFilterData.set({
      filterCode: "Active",
      sortCode: "clientName",
      groupCode: "Client",
    });
  }

  toggleSearch() {
    const newSearchVisible = !this.isSearchVisible();
    if (this.needsRefresh() && !newSearchVisible) {
      this.loading.set(true);
      void this.onSubmit(true);
      this.needsRefresh.set(false);
    }
    this.isSearchVisible.set(newSearchVisible);

    if (newSearchVisible) {
      addQueryParams({ search: true });
    } else {
      removeQueryParams("search");
      this.searchForm.resetToUndefined();
    }
  }

  async showAddWorkspace(
    engagementOption: EngagementOption,
    engagementId: string | undefined = undefined,
    ignoreExistingSnappedWindows = false,
  ) {
    if (this.isSupportUser()) {
      return;
    }

    const workspaceListContentWrapper = this.host;
    const el = workspaceListContentWrapper.nativeElement;

    const props: ICopyCloneAndRollforwardModalProps = {
      engagementOption,
    };

    if (engagementOption === EngagementOption.Edit) {
      if (engagementId) {
        props.engagementId = engagementId;
      } else {
        props.engagementId = this.selectedEngagements()[0]?.data.engagementId;
      }
    }

    const res = await this.workspaceListService.showAddWorkspace(
      engagementOption,
      props,
      ignoreExistingSnappedWindows ||
        engagementOption !== EngagementOption.Edit,
      el,
    );

    if (res) {
      await this.handleRefreshWithBCMessage();
    } else {
      this.focusService.focusFirst(el);
    }
  }

  async performSearch() {
    const workspaceListContentWrapper = this.host;
    const el = workspaceListContentWrapper.nativeElement;

    // Update fields, trigger query
    const formData = this.searchForm.getRawValue();
    formData.selectedMemberFirmId = formData.selectedMemberFirmId ?? "";
    formData.clientId = formData.clientId?.trim() ?? "";
    formData.clientName = formData.clientName?.trim() ?? "";
    formData.entityName = formData.entityName?.trim() ?? "";
    formData.workspaceDisplayName = formData.workspaceDisplayName?.trim() ?? "";
    formData.engagementId = formData.engagementId?.trim() ?? "";

    const modalPageData: WorkspaceSearchResultsContext = {
      ...formData,
      isSupportUser: this.isSupportUser(),
      isSnapshotUser: this.canViewEngagementSnapshots(),
      aptVersion: this.aptVersion(),
      workspaceListEl: el,
      modalPage: this.modalPage,
      reSearch$: this.reSearch,
    };

    if (this.workspaceSearchRef || this.searchDialogOpening) {
      if (this.workspaceSearchRef) {
        this.reSearch.next({
          ...formData,
          isSupportUser: this.isSupportUser(),
          isSnapshotUser: this.canViewEngagementSnapshots(),
        });
      }
      return;
    }

    this.searchDialogOpening = true;
    const [response, dialogRef] = await this.windowDialogService.showWithRef<
      WorkspaceSearchResultsResponse | undefined | boolean
    >(
      this.getSearchResultsContent,
      modalPageData,
      {
        canChangeSnap: false,
        canClose: true,
        canDrag: false,
        canMaximize: false,
        canMinimize: false,
        canOpenInNewWindow: false,
        canResizeX: "end",
        canResizeY: false,
        modal: true,
        snapDirection: "left",
        snapSize: "420px",
        heightVariant: "withinShellHeader",
      },
      undefined,
      this.childDialogRefs,
    );

    this.workspaceSearchRef = dialogRef;
    this.workspaceSearchRef.onClose.subscribe(() => {
      this.workspaceSearchRef = undefined;
      this.searchDialogOpening = false;
    });

    if (await response) {
      this.needsRefresh.set(true);
    }

    this.focusService.focusFirst(el);
  }

  async onSubmit(isNew: boolean): Promise<boolean> {
    this.engagementService.allEngagements.set(undefined);
    this.getFilteredItemsHasRan = false;
    this.canBeFinishedLoading = false;
    this.loading.set(true);
    await this.engagementService.getAllEngagements(
      this.aptVersion() === APTVersion.Classic,
    );
    return this.engagementsTableService.onSubmit(isNew);
  }

  async handleRefreshWithBCMessage() {
    this.allEngagementsBC.postMessage("refresh engagements");
    await this.onSubmit(true);
  }

  collapseAll() {
    const activeFilterData = this.activeFilterData();
    if (!activeFilterData) {
      return;
    }

    const listItemStates = this.workspaceListService.getListItemStates();

    for (const node of this.favoriteWorkspaces()) {
      collapseNodesRecursive(node, listItemStates);
    }

    for (const node of this.groupedWorkspaces()) {
      collapseNodesRecursive(node, listItemStates);
    }

    this.workspaceListService.setListItemStates(listItemStates);
    this.favoritesTree().updateSerializedValue();
    this.groupsTree().updateSerializedValue();
  }

  expandAll() {
    const activeFilterData = this.activeFilterData();
    if (!activeFilterData) {
      return;
    }

    const listItemStates = this.workspaceListService.getListItemStates();

    for (const node of this.favoriteWorkspaces()) {
      expandNodesRecursive(node, listItemStates);
    }

    for (const node of this.groupedWorkspaces()) {
      expandNodesRecursive(node, listItemStates);
    }

    this.workspaceListService.setListItemStates(listItemStates);
    this.favoritesTree().updateSerializedValue();
    this.groupsTree().updateSerializedValue();
  }

  onSelectionUpdated(
    fromTree: "fav" | "group",
    newSelection: WorkspaceTreeNode[],
  ) {
    this.selectedEngagements.set(newSelection);

    // Remove selection from the other tree
    const otherTree =
      fromTree === "fav" ? this.groupsTree() : this.favoritesTree();
    otherTree.primengTreeRef()?.selection.set(undefined);
  }

  async loadAllOnboardedMemberFirms() {
    this.onboardedFirms = [];
    const firms = await this.engagementService.fetchAllOnboardedMemberFirms();

    if (firms.length > 0) {
      this.onboardedFirms = firms.map((firm) => ({
        label: `${firm}`,
        value: `${firm}`,
        name: `${firm}`,
      }));
    }
  }

  @precacheComponent("onIdle")
  getSearchResultsContent() {
    return import(
      "src/app/workspace/workspace-search-results/workspace-search-results.component"
    );
  }
}

export default WorkspaceListComponent;
